import { useTranslation } from '@jq4race/i18n';
import illustrationBlazer from '@jq4race/ui/assets/illustrations/illustration-blazer.png';
import illustrationCipher from '@jq4race/ui/assets/illustrations/illustration-cipher.png';
import illustrationEnvelope from '@jq4race/ui/assets/illustrations/illustration-envelope.png';
import illustrationKeyTassel from '@jq4race/ui/assets/illustrations/illustration-key-tassel.png';
import illustrationLetter from '@jq4race/ui/assets/illustrations/illustration-letter.png';
import illustrationNotepad from '@jq4race/ui/assets/illustrations/illustration-notepad.png';
import illustrationPadlock from '@jq4race/ui/assets/illustrations/illustration-padlock.png';
import illustrationPodium from '@jq4race/ui/assets/illustrations/illustration-podium.png';
import illustrationValet from '@jq4race/ui/assets/illustrations/illustration-valet.png';
import logoPrimary from '@jq4race/ui/assets/logos/logo-primary.png';
import patternDecoFan from '@jq4race/ui/assets/patterns/pattern-cream-deco-fan.png';
import type { ReactNode } from 'react';

const ILLUSTRATIONS = {
  valet: illustrationValet,
  letter: illustrationLetter,
  cipher: illustrationCipher,
  padlock: illustrationPadlock,
  envelope: illustrationEnvelope,
  'key-tassel': illustrationKeyTassel,
  notepad: illustrationNotepad,
  blazer: illustrationBlazer,
  podium: illustrationPodium,
} as const;

type IllustrationKey = keyof typeof ILLUSTRATIONS;

interface AuthShellProps {
  title: string;
  eyebrow?: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  aside?: {
    illustration?: IllustrationKey;
    eyebrow?: string;
    headline?: string;
    body?: string;
  };
}

export function AuthShell({
  title,
  eyebrow,
  description,
  children,
  footer,
  aside,
}: AuthShellProps) {
  const { t } = useTranslation('auth');
  const defaultAside = {
    illustration: 'valet' as IllustrationKey,
    eyebrow: t('shell.defaultAside.eyebrow'),
    headline: t('shell.defaultAside.headline'),
    body: t('shell.defaultAside.body'),
  };
  const a = { ...defaultAside, ...aside };
  const illustrationSrc = ILLUSTRATIONS[a.illustration];

  return (
    <main className="min-h-screen bg-jq-paper text-foreground">
      <div className="grid min-h-screen lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)]">
        {/* ----- Form column ----- */}
        <section className="relative flex flex-col overflow-hidden px-6 py-10 sm:px-10 lg:px-16 lg:py-14">
          {/* Deco-fan pattern texture (desktop + mobile) */}
          <img
            src={patternDecoFan}
            alt=""
            aria-hidden
            className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-[0.13] [mask-image:radial-gradient(ellipse_at_center,black_65%,transparent_100%)]"
          />

          {/* Mobile-only top gold accent */}
          <span
            aria-hidden
            className="absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-jq-gold via-jq-gold-bright to-jq-gold lg:hidden"
          />

          <header className="relative flex items-center">
            <img
              src={logoPrimary}
              alt={t('shell.logoAlt')}
              className="h-20 w-auto sm:h-24 lg:h-28"
            />
          </header>

          <div className="relative flex flex-1 items-center justify-center py-10 lg:py-16">
            <div className="w-full max-w-md">
              {eyebrow ? <p className="jq-eyebrow jq-eyebrow--leader">{eyebrow}</p> : null}
              <h1 className="mt-4 font-editorial text-[40px] font-medium leading-[1.05] tracking-tight text-jq-navy sm:text-[48px] lg:text-[56px]">
                {title}
              </h1>
              {description ? (
                <p className="mt-3 text-[14px] leading-relaxed text-jq-graphite">{description}</p>
              ) : null}

              <div className="mt-8">{children}</div>

              {footer ? (
                <div className="mt-8 border-t border-jq-sand/60 pt-5 text-[14px] text-jq-graphite">
                  {footer}
                </div>
              ) : null}
            </div>
          </div>

          <footer className="relative mt-8 text-center text-[11px] uppercase tracking-wider text-jq-stone sm:text-[12px] lg:mt-0 lg:text-left">
            {t('shell.copyright', { year: new Date().getFullYear() })}
          </footer>
        </section>

        {/* ----- Brand / illustration column (desktop only) ----- */}
        <aside
          className="relative hidden overflow-hidden bg-jq-ink text-jq-paper lg:block"
          aria-hidden="true"
        >
          <img
            src={illustrationSrc}
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
          />

          <div className="absolute inset-x-0 bottom-0 h-[55%] bg-gradient-to-t from-jq-ink via-jq-ink/85 to-transparent" />

          <div className="absolute inset-x-12 top-12 flex items-center justify-end gap-3 xl:inset-x-16 xl:top-16">
            <span className="font-ceremony text-[12px] uppercase tracking-widest text-jq-gold-bright">
              {t('shell.estTradition')}
            </span>
            <span className="h-px w-10 bg-jq-gold/60" />
          </div>

          <div className="absolute inset-x-0 bottom-0 px-12 pb-14 xl:px-16 xl:pb-16">
            <p className="font-sans text-[11px] font-medium uppercase tracking-widest text-jq-gold-bright">
              {a.eyebrow}
            </p>
            <h2 className="mt-4 max-w-lg font-editorial text-[30px] font-medium leading-[1.15] text-jq-paper xl:text-[36px]">
              {a.headline}
            </h2>
            <p className="mt-4 max-w-md text-[14px] leading-relaxed text-jq-navy-200">{a.body}</p>

            <div className="mt-10 flex items-center justify-between text-[11px] uppercase tracking-widest text-jq-navy-200">
              <span>{t('shell.managerAdmin')}</span>
              <span className="flex items-center gap-2">
                <span className="h-1 w-1 rounded-full bg-jq-gold" />
                <span>{t('shell.privateEncrypted')}</span>
              </span>
            </div>
          </div>
        </aside>
      </div>
    </main>
  );
}
